import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import Logo from './Logo';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { language, changeLanguage, t } = useLanguage();
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <Logo />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors hover:text-emerald-600 ${
                isActive('/') ? 'text-emerald-600' : 'text-gray-700'
              }`}
            >
              {t.nav.home}
            </Link>
            <Link
              to="/comment-ca-marche"
              className={`text-sm font-medium transition-colors hover:text-emerald-600 ${
                isActive('/comment-ca-marche') ? 'text-emerald-600' : 'text-gray-700'
              }`}
            >
              {t.nav.howItWorks}
            </Link>
            <Link
              to="/nos-offres"
              className={`text-sm font-medium transition-colors hover:text-emerald-600 ${
                isActive('/nos-offres') ? 'text-emerald-600' : 'text-gray-700'
              }`}
            >
              {t.nav.offers}
            </Link>
            <Link
              to="/a-propos"
              className={`text-sm font-medium transition-colors hover:text-emerald-600 ${
                isActive('/a-propos') ? 'text-emerald-600' : 'text-gray-700'
              }`}
            >
              {t.nav.about}
            </Link>
            <Link
              to="/contact"
              className={`text-sm font-medium transition-colors hover:text-emerald-600 ${
                isActive('/contact') ? 'text-emerald-600' : 'text-gray-700'
              }`}
            >
              {t.nav.contact}
            </Link>
          </nav>

          {/* Desktop CTA and Language Switcher */}
          <div className="hidden md:flex items-center gap-4">
            <select
              value={language}
              onChange={(e) => changeLanguage(e.target.value)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium border border-gray-300 rounded-md hover:bg-gray-50 cursor-pointer"
            >
              <option value="fr">🇫🇷 FR</option>
              <option value="es">🇪🇸 ES</option>
              <option value="en">🇬🇧 EN</option>
              <option value="it">🇮🇹 IT</option>
              <option value="de">🇩🇪 DE</option>
              <option value="pt">🇵🇹 PT</option>
            </select>
            <Link to="/nos-offres">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-2 rounded-md font-medium transition-all duration-300 hover:scale-105 shadow-md">
                {t.nav.cta}
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 rounded-md text-gray-700 hover:bg-gray-100"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t">
          <nav className="px-4 py-6 space-y-4">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className={`block text-base font-medium transition-colors ${
                isActive('/') ? 'text-emerald-600' : 'text-gray-700'
              }`}
            >
              {t.nav.home}
            </Link>
            <Link
              to="/comment-ca-marche"
              onClick={() => setMobileMenuOpen(false)}
              className={`block text-base font-medium transition-colors ${
                isActive('/comment-ca-marche') ? 'text-emerald-600' : 'text-gray-700'
              }`}
            >
              {t.nav.howItWorks}
            </Link>
            <Link
              to="/nos-offres"
              onClick={() => setMobileMenuOpen(false)}
              className={`block text-base font-medium transition-colors ${
                isActive('/nos-offres') ? 'text-emerald-600' : 'text-gray-700'
              }`}
            >
              {t.nav.offers}
            </Link>
            <Link
              to="/a-propos"
              onClick={() => setMobileMenuOpen(false)}
              className={`block text-base font-medium transition-colors ${
                isActive('/a-propos') ? 'text-emerald-600' : 'text-gray-700'
              }`}
            >
              {t.nav.about}
            </Link>
            <Link
              to="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className={`block text-base font-medium transition-colors ${
                isActive('/contact') ? 'text-emerald-600' : 'text-gray-700'
              }`}
            >
              {t.nav.contact}
            </Link>
            <div className="pt-4 border-t space-y-4">
              <select
                value={language}
                onChange={(e) => { changeLanguage(e.target.value); setMobileMenuOpen(false); }}
                className="w-full px-3 py-2 text-sm font-medium border border-gray-300 rounded-md"
              >
                <option value="fr">🇫🇷 Français</option>
                <option value="es">🇪🇸 Español</option>
                <option value="en">🇬🇧 English</option>
                <option value="it">🇮🇹 Italiano</option>
                <option value="de">🇩🇪 Deutsch</option>
                <option value="pt">🇵🇹 Português</option>
              </select>
              <Link to="/nos-offres" onClick={() => setMobileMenuOpen(false)} className="block">
                <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white">
                  {t.nav.cta}
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;